import { registerLocaleData } from '@angular/common';
import { HttpBackend } from '@angular/common/http';
import { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ExtensionCommunicationService } from '@shared/extension';
import { getEnumValues } from '@shared/utils';
import { ITranslationResource } from 'ngx-translate-multi-http-loader';
import { firstValueFrom } from 'rxjs';
import { TranslationsLoader } from '../classes';
import { CURRENT_LANGUAGE_LOCAL_STORAGE_KEY, LANGUAGES, LOCALES_MAP } from '../constants';
import { Language } from '../enums';
import { LanguageData } from '../interfaces';

interface TranslationsLoadResult {
  code: string;
  values: Object;
}

export function getLanguageData(language: string): LanguageData | null {
  return LANGUAGES.find((item: LanguageData) => item.code === language);
}

export async function getCurrentLanguage(extensionCommunicationService: ExtensionCommunicationService): Promise<LanguageData> {
  let lang: string | undefined = await extensionCommunicationService.getLocalStorage(CURRENT_LANGUAGE_LOCAL_STORAGE_KEY);
  if (lang === '""') {
    lang = null;
  }
  if (lang) {
    lang = lang.replace(/"/g, '');
  } else {
    if (window && navigator) {
      lang =
        navigator.languages?.[0] ||
        navigator.language ||
        (navigator as any).browserLanguage ||
        (navigator as any).userLanguage;
      if (lang) {
        if (lang.includes('-')) {
          lang = lang.split('-')[0];
        } else if (lang.includes('_')) {
          lang = lang.split('_')[0];
        }
      }
    }
    if (!lang) {
      lang = Language.English;
    }
  }
  if (!getEnumValues(Language).includes(lang)) {
    lang = Language.English;
  }
  return getLanguageData(lang) as LanguageData;
}


export async function initializeTranslations(
  injector: Injector,
  assetsPath: string,
  version: string,
  translations: ITranslationResource[],
): Promise<Language> {
  const extensionCommunicationService = injector.get(
    ExtensionCommunicationService,
  );
  let languageData: LanguageData = await getCurrentLanguage(
    extensionCommunicationService,
  );
  let language: Language = languageData.code as Language;
  if (!LOCALES_MAP.has(language)) {
    languageData = getLanguageData(Language.English) as LanguageData;
    language = languageData.code as Language;
  }
  registerLocaleData(LOCALES_MAP.get(language));

  const translateService = injector.get(TranslateService);

  translateService.setDefaultLang(language);
  translateService.addLangs(LANGUAGES.map((item: LanguageData) => item.code));

  const promises: Array<Promise<TranslationsLoadResult>> = [];
  LANGUAGES.forEach((data: LanguageData) => {
    const resources: ITranslationResource[] = translations.map(
      (item: ITranslationResource) => {
        const prefix: string =
          assetsPath + item.prefix.replace('{{lang}}', data.code);
        const suffix: string = item.suffix + `?v${version}`;
        return { ...item, prefix, suffix };
      },
    );
    const translateLoader = new TranslationsLoader(injector.get(HttpBackend), resources, version);
    promises.push(
      firstValueFrom(translateLoader.getTranslation(data.code)).then(
        (values: Object) =>
          ({ code: data.code, values }) as TranslationsLoadResult,
      ),
    );
  });

  const results: Array<PromiseSettledResult<TranslationsLoadResult>> = await Promise.allSettled(promises);
  results.forEach((result: PromiseSettledResult<TranslationsLoadResult>) => {
    if (result.status === 'fulfilled') {
      translateService.setTranslation(result.value.code, result.value.values, false);
    }
  });

  translateService.use(language);
  return language;
}
